/**
 * clientSummary — agregações puras do Resumo do Cliente.
 *
 * Fora do componente de propósito (mesmo padrão de `calendarEvents.ts`,
 * `stock.ts` e `paymentsReport.ts`): tudo aqui é função pura sobre os arrays já
 * carregados, testável sem renderizar nada e sem tocar em Supabase.
 *
 * Regras que este módulo existe para não deixar quebrar em silêncio:
 *
 * 1. **Venda de PDV NÃO é proposta.** Uma venda rápida é um `quotes` com
 *    `document_type='pedido'` que já nasce `completed` (ver `quickSale.ts`).
 *    Ela ENTRA no LTV (é dinheiro que entrou) mas fica FORA do denominador da
 *    taxa de conversão — senão a taxa sobe sozinha a cada venda de balcão e
 *    passa a mentir sobre a eficiência das propostas.
 * 2. **Divisão por zero não vira `NaN` na tela.** Cliente sem nenhum documento
 *    fechado tem ticket médio `null` (e não `NaN`); cliente sem nenhuma
 *    proposta tem conversão `null`. Quem renderiza decide o texto do vazio.
 * 3. **Datas date-only nunca passam por `new Date(...)`.** `receipts.issue_date`
 *    e `finance_entries.due_date` são colunas DATE ('YYYY-MM-DD'); em UTC-3
 *    `new Date('2026-07-25')` cai no dia 24. Usamos
 *    `localDateFromIsoDatePart` (ver `calendarEvents.ts`).
 * 4. **`total` é nullable** em `quotes` — `Number(null)` é 0, o que aqui é o
 *    comportamento certo (documento sem valor não soma), mas só porque é
 *    explícito. Nunca somar `q.total` cru.
 * 5. **Financeiro ausente ≠ financeiro zerado.** `financeEntries: null`
 *    significa "este usuário não pode ler `finance_entries`" (RLS é
 *    owner/admin-only) e produz `finance: null` — a tela ESCONDE o bloco em vez
 *    de mostrar "R$ 0,00", que seria uma informação falsa.
 */
import { localDateFromIsoDatePart } from '@/lib/calendarEvents';
import { computeFinanceStatus } from '@/lib/financeStatus';
import { NFSE_STATUS_META } from '@/types/fiscal';

// ===========================================================================
// Tipos
// ===========================================================================

export type ClientTimelineKind =
  | 'quote'
  | 'sale'
  | 'service_order'
  | 'contract'
  | 'receipt'
  | 'appointment'
  | 'nfse';

export interface ClientTimelineEntry {
  id: string;
  kind: ClientTimelineKind;
  title: string;
  /** Data LOCAL que cai no dia de calendário verdadeiro do documento. */
  date: Date;
  /** Status cru da linha de origem (traduzido por `timelineStatusLabel`). */
  status: string;
  /** Valor do documento, ou `null` quando o tipo não tem valor (compromisso). */
  amount: number | null;
  /** Rota interna para abrir o documento. */
  route: string;
  /** A linha original. */
  ref: any;
}

/**
 * As sete origens do resumo. Todas já filtradas por `client_id` e
 * `company_id` por quem carrega (ver `useClientSummary`).
 *
 * `financeEntries` é `null` quando o papel do usuário não pode ler o financeiro
 * — distinto de `[]`, que significa "pode ler e não há nada".
 */
export interface ClientSummarySources {
  quotes?: any[];
  serviceOrders?: any[];
  contracts?: any[];
  receipts?: any[];
  appointments?: any[];
  nfseDocuments?: any[];
  financeEntries?: any[] | null;
}

export interface ClientFinanceMetrics {
  /** Em aberto e ainda não vencido. */
  receivable: number;
  /** Em aberto e com vencimento no passado. */
  overdue: number;
  overdueCount: number;
}

export interface ClientMetrics {
  /** Orçamentos aprovados/concluídos + vendas de PDV. O que de fato entrou. */
  lifetimeValue: number;
  /** Quantos documentos fecharam (denominador do ticket médio). */
  closedCount: number;
  /** `null` quando não há documento fechado — nunca `NaN`. */
  averageTicket: number | null;
  /** Propostas enviadas (exclui vendas de PDV). */
  proposalsCount: number;
  approvedProposalsCount: number;
  /** Fração 0..1. `null` quando não houve nenhuma proposta. */
  conversionRate: number | null;
  /** Soma dos orçamentos ainda pendentes. */
  openValue: number;
  openCount: number;
  /** Documento mais recente de qualquer tipo. */
  lastInteraction: Date | null;
  /** `null` quando o usuário não pode ler `finance_entries` (gate de papel). */
  finance: ClientFinanceMetrics | null;
  totalDocuments: number;
  countsByKind: Record<ClientTimelineKind, number>;
}

// ===========================================================================
// Rótulos
// ===========================================================================

export const TIMELINE_KIND_LABELS: Record<ClientTimelineKind, string> = {
  quote: 'Orçamento',
  sale: 'Venda',
  service_order: 'Ordem de serviço',
  contract: 'Contrato',
  receipt: 'Recibo',
  appointment: 'Compromisso',
  nfse: 'Nota fiscal',
};

/**
 * Rótulos de status por tipo. Ficam aqui (e não importados dos hooks) porque
 * este módulo é puro: `SERVICE_ORDER_STATUSES` mora em `useServiceOrders.ts`,
 * que arrasta Supabase/TanStack para dentro do bundle de um agregador. Os
 * textos espelham exatamente os das telas de origem — se um status novo
 * aparecer, o `?? status` abaixo mostra o valor cru em vez de sumir.
 */
const STATUS_LABELS: Record<ClientTimelineKind, Record<string, string>> = {
  quote: {
    pending: 'Pendente',
    approved: 'Aprovado',
    completed: 'Concluído',
    cancelled: 'Cancelado',
    rejected: 'Rejeitado',
  },
  sale: {
    completed: 'Concluída',
    cancelled: 'Cancelada',
  },
  service_order: {
    pending: 'Pendente',
    in_progress: 'Em andamento',
    completed: 'Concluída',
    cancelled: 'Cancelada',
  },
  contract: {
    draft: 'Rascunho',
    sent: 'Enviado',
    partially_signed: 'Parcial',
    signed: 'Assinado',
    cancelled: 'Cancelado',
  },
  receipt: {
    issued: 'Emitido',
    cancelled: 'Cancelado',
  },
  appointment: {
    scheduled: 'Agendado',
    done: 'Concluído',
    cancelled: 'Cancelado',
  },
  nfse: {},
};

export function timelineKindLabel(kind: ClientTimelineKind): string {
  return TIMELINE_KIND_LABELS[kind] ?? String(kind);
}

export function timelineStatusLabel(kind: ClientTimelineKind, status: string): string {
  // NFS-e tem uma máquina de estados própria e já exaustivamente rotulada.
  if (kind === 'nfse') {
    return NFSE_STATUS_META[status as keyof typeof NFSE_STATUS_META]?.label ?? String(status);
  }
  return STATUS_LABELS[kind]?.[status] ?? String(status);
}

// ===========================================================================
// Helpers
// ===========================================================================

/** Uma venda rápida é um `quotes` com `document_type='pedido'`. */
export function isSaleRow(quote: any): boolean {
  return quote?.document_type === 'pedido';
}

/** Documento que representa dinheiro fechado (entra no LTV e no ticket médio). */
function isClosedQuote(quote: any): boolean {
  const status = quote?.status;
  return status === 'approved' || status === 'completed';
}

function money(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Data de um timestamp (`created_at`, `starts_at`, …). Devolve `null` quando
 * ausente ou inválido, para a linha ser descartada em vez de virar `Invalid Date`
 * e envenenar a ordenação.
 */
function instantOrNull(value: unknown): Date | null {
  if (!value) return null;
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Data de uma coluna DATE ('YYYY-MM-DD'), sem deslocar o dia em UTC-3. */
function localDateOrNull(value: unknown): Date | null {
  if (!value) return null;
  const date = localDateFromIsoDatePart(String(value));
  return Number.isNaN(date.getTime()) ? null : date;
}

function firstNonEmpty(...candidates: unknown[]): string {
  for (const candidate of candidates) {
    const text = typeof candidate === 'string' ? candidate.trim() : '';
    if (text) return text;
  }
  return '';
}

// ===========================================================================
// Linha do tempo
// ===========================================================================

/**
 * Funde os sete tipos de documento numa lista cronológica única, do mais
 * recente para o mais antigo (é um histórico: o que acabou de acontecer vem
 * primeiro). Linhas sem data utilizável são descartadas — uma entrada com
 * `Invalid Date` estragaria a ordenação inteira e a "última interação".
 *
 * A data de cada tipo é a que o usuário reconhece como "a data do documento":
 * o recibo usa `issue_date` (que ele mesmo informa), o compromisso usa
 * `starts_at`, a NFS-e usa a emissão. Todos caem em `created_at` quando a
 * coluna preferida está vazia.
 */
export function buildClientTimeline(sources: ClientSummarySources): ClientTimelineEntry[] {
  const entries: ClientTimelineEntry[] = [];

  for (const quote of sources.quotes ?? []) {
    if (!quote?.id) continue;
    const date = instantOrNull(quote.created_at);
    if (!date) continue;
    const sale = isSaleRow(quote);
    entries.push({
      id: String(quote.id),
      kind: sale ? 'sale' : 'quote',
      title: firstNonEmpty(quote.title, sale ? 'Venda rápida' : 'Orçamento'),
      date,
      status: quote.status ?? 'pending',
      amount: money(quote.total),
      route: `/quotes/${quote.id}`,
      ref: quote,
    });
  }

  for (const order of sources.serviceOrders ?? []) {
    if (!order?.id) continue;
    const date = instantOrNull(order.created_at);
    if (!date) continue;
    entries.push({
      id: String(order.id),
      kind: 'service_order',
      title: firstNonEmpty(
        order.title,
        order.sequence_id ? `OS #${order.sequence_id}` : '',
        'Ordem de serviço',
      ),
      date,
      status: order.status ?? 'pending',
      amount: money(order.total),
      route: `/ordens-servico/${order.id}`,
      ref: order,
    });
  }

  for (const contract of sources.contracts ?? []) {
    if (!contract?.id) continue;
    const date = instantOrNull(contract.created_at);
    if (!date) continue;
    entries.push({
      id: String(contract.id),
      kind: 'contract',
      title: firstNonEmpty(
        contract.title,
        contract.sequence_id ? `Contrato #${contract.sequence_id}` : '',
        'Contrato',
      ),
      date,
      status: contract.status ?? 'draft',
      amount: money(contract.total),
      route: `/contratos/${contract.id}`,
      ref: contract,
    });
  }

  for (const receipt of sources.receipts ?? []) {
    if (!receipt?.id) continue;
    // `issue_date` é DATE — jamais `new Date(issue_date)`.
    const date = localDateOrNull(receipt.issue_date) ?? instantOrNull(receipt.created_at);
    if (!date) continue;
    entries.push({
      id: String(receipt.id),
      kind: 'receipt',
      title: firstNonEmpty(
        receipt.description,
        receipt.sequence_id ? `Recibo #${receipt.sequence_id}` : '',
        'Recibo',
      ),
      date,
      // `receipts` não tem coluna de status: o cancelamento é o carimbo.
      status: receipt.cancelled_at ? 'cancelled' : 'issued',
      amount: money(receipt.amount),
      route: `/recibos/${receipt.id}`,
      ref: receipt,
    });
  }

  for (const appointment of sources.appointments ?? []) {
    if (!appointment?.id) continue;
    const date = instantOrNull(appointment.starts_at) ?? instantOrNull(appointment.created_at);
    if (!date) continue;
    entries.push({
      id: String(appointment.id),
      kind: 'appointment',
      title: firstNonEmpty(appointment.title, 'Compromisso'),
      date,
      status: appointment.status ?? 'scheduled',
      // Compromisso não tem valor — `null` para a tela não escrever "R$ 0,00".
      amount: null,
      // A Agenda não tem rota de detalhe por id; abre o calendário.
      route: '/agenda',
      ref: appointment,
    });
  }

  for (const nfse of sources.nfseDocuments ?? []) {
    if (!nfse?.id) continue;
    const date = instantOrNull(nfse.emitida_em) ?? instantOrNull(nfse.created_at);
    if (!date) continue;
    entries.push({
      id: String(nfse.id),
      kind: 'nfse',
      title: firstNonEmpty(nfse.numero ? `NFS-e ${nfse.numero}` : '', 'Nota fiscal de serviço'),
      date,
      status: nfse.status ?? 'draft',
      amount: money(nfse.valor_servico),
      // O módulo fiscal abre a nota por diálogo, não por rota de detalhe.
      route: '/nfse',
      ref: nfse,
    });
  }

  // Mais recente primeiro. Desempate por id para a ordem ser determinística
  // (dois documentos criados no mesmo instante não podem trocar de lugar entre
  // renders, senão a lista "pisca" ao revalidar).
  entries.sort((a, b) => {
    const diff = b.date.getTime() - a.date.getTime();
    return diff !== 0 ? diff : a.id.localeCompare(b.id);
  });
  return entries;
}

// ===========================================================================
// Indicadores
// ===========================================================================

const EMPTY_COUNTS: Record<ClientTimelineKind, number> = {
  quote: 0,
  sale: 0,
  service_order: 0,
  contract: 0,
  receipt: 0,
  appointment: 0,
  nfse: 0,
};

/**
 * Indicadores do relacionamento. `today` é injetável para o teste não depender
 * do relógio (mesma convenção de `computeFinanceStatus`).
 */
export function computeClientMetrics(
  sources: ClientSummarySources,
  today?: string,
): ClientMetrics {
  const quotes = sources.quotes ?? [];

  // --- LTV e ticket médio: orçamentos fechados + vendas de PDV --------------
  // Vendas nascem `completed`, então o mesmo filtro de status já as inclui.
  const closed = quotes.filter(isClosedQuote);
  const lifetimeValue = closed.reduce((sum, q) => sum + money(q.total), 0);
  const closedCount = closed.length;

  // --- Conversão: SÓ propostas no denominador ------------------------------
  const proposals = quotes.filter((q) => !isSaleRow(q));
  const approvedProposals = proposals.filter(isClosedQuote);
  const proposalsCount = proposals.length;

  // --- Em aberto: propostas ainda pendentes --------------------------------
  const open = proposals.filter((q) => (q.status ?? 'pending') === 'pending');
  const openValue = open.reduce((sum, q) => sum + money(q.total), 0);

  // --- Financeiro (pode estar fora de alcance por papel) --------------------
  let finance: ClientFinanceMetrics | null = null;
  if (sources.financeEntries) {
    let receivable = 0;
    let overdue = 0;
    let overdueCount = 0;
    for (const entry of sources.financeEntries) {
      // "A receber" é receita. Uma despesa vinculada ao cliente não é algo que
      // ele deve — somá-la aqui inflaria a cobrança.
      if (entry?.kind !== 'income') continue;
      // `computed_status` vem da VIEW; o espelho local só entra quando a linha
      // veio do cache offline sem a coluna computada.
      const status =
        entry.computed_status ?? computeFinanceStatus(entry.status, entry.due_date, today);
      if (status === 'pending') {
        receivable += money(entry.amount);
      } else if (status === 'overdue') {
        overdue += money(entry.amount);
        overdueCount += 1;
      }
    }
    finance = { receivable, overdue, overdueCount };
  }

  // --- Última interação e contagens ----------------------------------------
  const timeline = buildClientTimeline(sources);
  const countsByKind = { ...EMPTY_COUNTS };
  for (const entry of timeline) {
    countsByKind[entry.kind] += 1;
  }

  return {
    lifetimeValue,
    closedCount,
    averageTicket: closedCount > 0 ? lifetimeValue / closedCount : null,
    proposalsCount,
    approvedProposalsCount: approvedProposals.length,
    conversionRate: proposalsCount > 0 ? approvedProposals.length / proposalsCount : null,
    openValue,
    openCount: open.length,
    // A timeline já está ordenada do mais recente para o mais antigo.
    lastInteraction: timeline.length > 0 ? timeline[0].date : null,
    finance,
    totalDocuments: timeline.length,
    countsByKind,
  };
}
